import { Box, Text, HStack, VStack } from '@chakra-ui/react';

interface AIScoreBarProps {
  /** スコア（0-100） */
  score: number;
  /** ラベル */
  label?: string;
  /** バーの高さ */
  height?: string;
  /** 色のパレット */
  colorScheme?: 'blue' | 'green' | 'orange' | 'red';
  /** スコア表示を非表示にするか */
  hideScore?: boolean;
}

/**
 * スコアに基づいて色を決定
 */
function getScoreColor(score: number): string {
  if (score >= 80) return 'green.500';
  if (score >= 60) return 'blue.500';
  if (score >= 40) return 'orange.500';
  return 'red.500';
}

/**
 * スコアに基づいて背景色を決定
 */
function getScoreBackgroundColor(score: number): string {
  if (score >= 80) return 'green.100';
  if (score >= 60) return 'blue.100';
  if (score >= 40) return 'orange.100';
  return 'red.100';
}

/**
 * AIスコア表示バーコンポーネント
 */
export function AIScoreBar({
  score,
  label,
  height = '8px',
  colorScheme,
  hideScore = false,
}: AIScoreBarProps) {
  const normalizedScore = Math.min(100, Math.max(0, score));
  const color = colorScheme ? `${colorScheme}.500` : getScoreColor(normalizedScore);
  const bgColor = colorScheme ? `${colorScheme}.100` : getScoreBackgroundColor(normalizedScore);

  return (
    <VStack align="stretch" gap={1} width="100%">
      {label && (
        <HStack justify="space-between">
          <Text fontSize="xs" color="gray.600">
            {label}
          </Text>
          {!hideScore && (
            <Text fontSize="xs" fontWeight="medium" color={color}>
              {normalizedScore}
            </Text>
          )}
        </HStack>
      )}
      <Box
        width="100%"
        height={height}
        bg={bgColor}
        borderRadius="full"
        overflow="hidden"
      >
        <Box
          width={`${normalizedScore}%`}
          height="100%"
          bg={color}
          borderRadius="full"
          transition="width 0.3s ease"
        />
      </Box>
    </VStack>
  );
}

interface AIScoreCircleProps {
  /** スコア（0-100） */
  score: number;
  /** サイズ */
  size?: 'sm' | 'md' | 'lg';
  /** ラベル */
  label?: string;
}

/**
 * 円形スコア表示コンポーネント
 */
export function AIScoreCircle({ score, size = 'md', label }: AIScoreCircleProps) {
  const normalizedScore = Math.min(100, Math.max(0, score));
  const color = getScoreColor(normalizedScore);

  const sizeMap = {
    sm: { box: '48px', font: 'md', labelFont: 'xs' },
    md: { box: '64px', font: 'xl', labelFont: 'sm' },
    lg: { box: '80px', font: '2xl', labelFont: 'sm' },
  };

  const { box, font, labelFont } = sizeMap[size];

  return (
    <VStack gap={1}>
      <Box
        width={box}
        height={box}
        borderRadius="full"
        border="4px solid"
        borderColor={color}
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="white"
      >
        <Text fontSize={font} fontWeight="bold" color={color}>
          {normalizedScore}
        </Text>
      </Box>
      {label && (
        <Text fontSize={labelFont} color="gray.600" textAlign="center">
          {label}
        </Text>
      )}
    </VStack>
  );
}
